import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import StyledContainer from "@/components/StyledContainer";
import SocialLinks from "@/components/StyledFooter/SocialLinks";
import EnFlag from "./en-flag.svg";
import EsFlag from "./es-flag.svg";
import Image from "next/image";
import Link from "next/link";

const TopBar = ({
  stickyMenu,
  settings,
  scrollPosition,
  otherLanguages = [],
}) => {
  return (
    <div
      className={clsx({
        "block relative visible text-white border-b border-b-[rgba(255,255,255,0.3)] transition-all": true,
        "!none !hidden": stickyMenu,
        "xl:block xl:visible": scrollPosition.top > 200 && !stickyMenu,
      })}
    >
      <StyledContainer>
        <div className="flex items-center">
          <a
            href="https://maps.app.goo.gl/Y69vLMxByvwmbN8CA"
            className={
              "hover:text-primary h-10 leading-[40px] border-r border-r-[rgba(255,255,255,0.3)] pe-5 hidden lg:block"
            }
          >
            {settings.data.address}
          </a>
          <p className={" hidden lg:block"}>
            <a
              href={`mailto:${settings.data.email_address}`}
              className={
                "inline-block h-10 leading-[40px] hover:text-primary transition-all px-5"
              }
            >
              {settings.data.email_address}
            </a>
          </p>
          <div className="flex items-center ms-auto">
            <PrismicLangSwitcher otherLanguages={otherLanguages} />

            <div className={"hidden lg:block"}>
              <SocialLinks links={settings.data.social_profiles} header />
            </div>
          </div>
        </div>
      </StyledContainer>
    </div>
  );
};

export default TopBar;

const languages = [
  {
    key: "en",
    prismicKey: "en-us",
    label: "English",
    url: "/",
    icon: EnFlag,
  },
  {
    key: "es",
    label: "Spanish",
    prismicKey: "es-es",
    url: "/es",
    icon: EsFlag,
  },
];

const PrismicLangSwitcher = () => {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const [curr, setCurr] = useState(languages[0]);

  const close = () => setOpen(false);

  useEffect(() => {
    window.addEventListener("click", close);

    const langKey = getLangKey();

    setCurr(languages.find((l) => l.key === langKey) || languages?.[0]);

    return () => window.removeEventListener("click", close);
  }, []);

  return (
    <div
      className={"relative inline-block text-left z-[999]"}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        onClick={() => setOpen(!open)}
        className={
          "cursor-pointer text-white bg-white bg-opacity-25 h-10 leading-[40px] flex px-4 md:me-8"
        }
        role={"button"}
      >
        {curr.icon && (
          <Image
            src={curr.icon}
            alt={"English flag"}
            className={"me-2"}
            height={24}
            width={24}
            priority
            loading={"eager"}
          />
        )}
        {curr.label || "English"}
      </span>

      {open && (
        <div
          className="absolute right-0 md:right-[32px] z-10 top-[110%] w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabIndex="-1"
        >
          <div className={"py-1"} role="none">
            {languages.map(({ label, icon, prismicKey }) => {
              return (
                <Link
                  key={label}
                  href={getTargetUrl(prismicKey, pathname)}
                  className={clsx({
                    "flex px-4 py-2 text-sm text-gray-700 text-center hover:bg-gray-300": true,
                    "bg-gray-300": curr.label === label,
                  })}
                  role="menuitem"
                  tabIndex="-1"
                  id="menu-item-0"
                  aria-label="menu-item-0"
                >
                  <Image
                    src={icon}
                    priority
                    loading={"eager"}
                    alt={label}
                    className={"me-2"}
                    height={24}
                    width={24}
                  />{" "}
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getTargetUrl = (prismicKey, pathname) => {
  const isEs = pathname.startsWith("/es");
  if (prismicKey === "es-es") {
    return isEs ? pathname : "/es" + pathname;
  }
  return isEs ? pathname.slice(3) || "/" : pathname;
};

const getLangKey = () => {
  const url = window.location?.href || "";

  if (url.includes("/es")) {
    return "es";
  }
  return "en";
};
